import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useEmpresa } from "@/contexts/EmpresaContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

// Catálogos de escuela. Esta página solo es accesible en empresas tipo escuela
// (ROUTE_ACCESS.escuela) y el backend rechaza todo /escuela/* en las demás.

const SIN_CENTRO = "__ninguno__";

const TOUR_STORAGE_KEY = "chkr_tour_escuela_seen";

const TOUR_STEPS = [
  { target: "[data-tour='esc-tabs']", title: "Primero se configura la escuela", content: "Antes de capturar la carga de los docentes, llena estas pestañas en orden: Reglas, Ciclos, Módulos, Materias y Grupos. Se capturan una sola vez y se reutilizan para todos los docentes." },
  { target: "[data-tour='esc-tab-configuracion']", title: "Reglas", content: "Cuántos minutos de retardo se toleran por clase (hasta ese límite la hora-clase se paga, después cuenta como falta), si el docente checa por bloque o en cada clase, la tarifa por hora-clase y si puede checar desde su portal." },
  { target: "[data-tour='esc-tab-ciclos']", title: "Ciclos escolares", content: "El periodo en que vale una carga horaria, por ejemplo \"2026-B\", de agosto a diciembre. Cada ciclo nuevo tiene su propia carga; puedes copiarla del ciclo anterior." },
  { target: "[data-tour='esc-tab-modulos']", title: "Módulos", content: "Los horarios fijos de clase de cada plantel y turno, incluidos los recesos: Módulo 1 de 7:00 a 7:50, Módulo 2 de 7:50 a 8:40… Son las filas de la cuadrícula en Carga docente: ahí solo marcas en qué módulo da clase cada maestro, sin escribir horas." },
  { target: "[data-tour='esc-tab-materias']", title: "Materias", content: "Las asignaturas que se imparten (Matemáticas, Historia…). Sirven para saber a qué clase faltó un docente y para el reporte por materia." },
  { target: "[data-tour='esc-tab-grupos']", title: "Grupos", content: "Opcional: 2°A, 3°B… Si los capturas, la asistencia y los reportes también dicen a qué grupo se le dio o no la clase." },
];

const AYUDA = {
  ciclos: "Un ciclo es el periodo en que vale una carga horaria (por ejemplo \"2026-B\", de agosto a diciembre). La carga de cada docente se captura por ciclo, y al empezar uno nuevo se puede copiar la del anterior.",
  modulos: "Los módulos son los horarios fijos de clase de cada plantel y turno, incluidos los recesos: Módulo 1 de 7:00 a 7:50, Módulo 2 de 7:50 a 8:40, Receso de 9:30 a 10:00… Se capturan una sola vez y forman las filas de la cuadrícula de Carga docente, donde solo eliges en qué módulo da clase cada maestro, sin escribir horas. Si un docente tiene una clase fuera de estos horarios, en Carga docente usa \"Horario libre\".",
  materias: "Las asignaturas que se imparten. Al capturar la carga, cada clase lleva su materia; así la asistencia dice a qué clase faltó el docente y el reporte agrupa por materia.",
  grupos: "Opcional. Si capturas los grupos (2°A, 3°B…), cada clase de la carga puede llevar su grupo y los reportes dicen a qué grupo se le dio o no la clase. Sin centro, el grupo sirve en todos los planteles.",
};

const CATALOGOS = {
  ciclos: {
    singular: "ciclo escolar",
    vacio: { nombre: "", fecha_inicio: "", fecha_fin: "" },
    columnas: [
      { titulo: "Ciclo", valor: (it) => it.nombre },
      { titulo: "Inicio", valor: (it) => it.fecha_inicio },
      { titulo: "Fin", valor: (it) => it.fecha_fin },
    ],
    campos: [
      { clave: "nombre", label: "Nombre (p.ej. 2026-B)", ancho: true },
      { clave: "fecha_inicio", label: "Inicio", tipo: "date" },
      { clave: "fecha_fin", label: "Fin", tipo: "date" },
    ],
  },
  modulos: {
    singular: "módulo",
    vacio: { centro_id: "", turno: "matutino", nombre: "", hora_inicio: "", hora_fin: "", tipo: "clase" },
    columnas: [
      { titulo: "Centro", valor: (it, ctx) => ctx.centroName(it.centro_id) },
      { titulo: "Turno", valor: (it) => it.turno },
      { titulo: "Módulo", valor: (it) => it.nombre },
      { titulo: "Horario", valor: (it) => `${it.hora_inicio}–${it.hora_fin}` },
      { titulo: "Tipo", valor: (it) => (it.tipo === "receso" ? "Receso" : "Clase") },
    ],
    campos: [
      { clave: "centro_id", label: "Centro", tipo: "centro", ancho: true },
      { clave: "turno", label: "Turno (matutino, vespertino…)" },
      { clave: "nombre", label: "Nombre (p.ej. Módulo 1)" },
      { clave: "hora_inicio", label: "Inicio", tipo: "time" },
      { clave: "hora_fin", label: "Fin", tipo: "time" },
      { clave: "tipo", label: "Tipo", tipo: "select", opciones: [["clase", "Clase"], ["receso", "Receso"]] },
    ],
  },
  materias: {
    singular: "materia",
    vacio: { nombre: "", clave: "" },
    columnas: [
      { titulo: "Materia", valor: (it) => it.nombre },
      { titulo: "Clave", valor: (it) => it.clave || "—" },
    ],
    campos: [
      { clave: "nombre", label: "Nombre", ancho: true },
      { clave: "clave", label: "Clave (opcional)" },
    ],
  },
  grupos: {
    singular: "grupo",
    vacio: { nombre: "", centro_id: null, turno: "" },
    columnas: [
      { titulo: "Grupo", valor: (it) => it.nombre },
      { titulo: "Centro", valor: (it, ctx) => (it.centro_id ? ctx.centroName(it.centro_id) : "—") },
      { titulo: "Turno", valor: (it) => it.turno || "—" },
    ],
    campos: [
      { clave: "nombre", label: "Nombre (p.ej. 2°A)" },
      { clave: "turno", label: "Turno (opcional)" },
      { clave: "centro_id", label: "Centro (opcional)", tipo: "centro", opcional: true, ancho: true },
    ],
  },
};

function Campo({ campo, form, setForm, centros }) {
  const valor = form[campo.clave];
  const set = (v) => setForm({ ...form, [campo.clave]: v });
  let control;
  if (campo.tipo === "centro") {
    control = (
      <Select value={valor || (campo.opcional ? SIN_CENTRO : "")} onValueChange={(v) => set(v === SIN_CENTRO ? null : v)}>
        <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
        <SelectContent>
          {campo.opcional && <SelectItem value={SIN_CENTRO}>Todos los centros</SelectItem>}
          {centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre || "(centro sin nombre)"}</SelectItem>)}
        </SelectContent>
      </Select>
    );
    if (centros.length === 0) {
      control = <p className="text-sm text-amber-700">No hay centros de trabajo activos en esta empresa. Da de alta el plantel en Centros de Trabajo.</p>;
    }
  } else if (campo.tipo === "select") {
    control = (
      <Select value={valor} onValueChange={set}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {campo.opciones.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else {
    control = <Input type={campo.tipo || "text"} value={valor || ""} onChange={(e) => set(e.target.value)} data-testid={`esc-campo-${campo.clave}`} />;
  }
  return (
    <div className={`space-y-1 ${campo.ancho ? "sm:col-span-2" : ""}`}>
      <Label className="label-uppercase">{campo.label}</Label>
      {control}
    </div>
  );
}

function CatalogoTab({ catalogo, centros, empresaActivaId }) {
  const def = CATALOGOS[catalogo];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(def.vacio);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/escuela/${catalogo}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogo, empresaActivaId]);

  const ctx = { centroName: (id) => { const c = centros.find((x) => x.id === id); return c ? c.nombre || "(sin nombre)" : "—"; } };

  const startCreate = () => {
    setEditing(null);
    setForm({ ...def.vacio, ...(def.vacio.centro_id === "" ? { centro_id: centros[0]?.id || "" } : {}) });
    setOpen(true);
  };
  const startEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/escuela/${catalogo}/${editing.id}`, form);
      else await api.post(`/escuela/${catalogo}`, { ...form, empresa_id: empresaActivaId || undefined });
      toast.success(editing ? "Cambios guardados" : "Registro creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.nombre}"?`)) return;
    try {
      await api.delete(`/escuela/${catalogo}/${it.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
        <p className="text-sm text-slate-600 max-w-3xl" data-testid={`esc-ayuda-${catalogo}`}>{AYUDA[catalogo]}</p>
        <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid={`esc-nuevo-${catalogo}`}>
          <Plus size={16} weight="bold" className="mr-2" /> Nuevo {def.singular}
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid={`esc-tabla-${catalogo}`}>
            <thead>
              <tr>
                {def.columnas.map((c) => <th key={c.titulo}>{c.titulo}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={def.columnas.length + 1} className="text-center text-slate-500 py-8">Sin registros</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id}>
                  {def.columnas.map((c) => <td key={c.titulo}>{c.valor(it, ctx)}</td>)}
                  <td className="whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(it)}><PencilSimple size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(it)} title="Eliminar"><Trash size={16} className="text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Editar ${def.singular}` : `Nuevo ${def.singular}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {def.campos.map((c) => <Campo key={c.clave} campo={c} form={form} setForm={setForm} centros={centros} />)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="esc-guardar">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfiguracionTab({ empresaActivaId }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/escuela/configuracion").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [empresaActivaId]);

  if (!form) return <div className="text-slate-500 text-sm">Cargando…</div>;

  const numero = (clave) => (e) => setForm({ ...form, [clave]: e.target.value === "" ? "" : Number(e.target.value) });

  const submit = async () => {
    setBusy(true);
    try {
      const data = { ...form, tarifa_hora_clase: form.tarifa_hora_clase === "" ? null : form.tarifa_hora_clase };
      const r = await api.put("/escuela/configuracion", data, { params: empresaActivaId ? { empresa_id: empresaActivaId } : {} });
      setForm(r.data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 max-w-2xl" data-testid="esc-configuracion">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-1">
          <Label className="label-uppercase">Minutos de tolerancia por clase</Label>
          <Input type="number" min={0} max={60} value={form.minutos_retardo_clase} onChange={numero("minutos_retardo_clase")} data-testid="esc-minutos-retardo" />
          <p className="text-xs text-slate-500">
            Con un retardo de hasta {form.minutos_retardo_clase || 0} min la hora-clase se paga completa; a partir de {(Number(form.minutos_retardo_clase) || 0) + 1} min cuenta como falta.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Forma de checar</Label>
          <Select value={form.modo_checado} onValueChange={(v) => setForm({ ...form, modo_checado: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tramo">Por bloque de clases seguidas</SelectItem>
              <SelectItem value="estricto">Una marca en cada clase</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">Por bloque: el docente checa al inicio y al final de cada grupo de clases contiguas.</p>
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Checar desde (min antes de la clase)</Label>
          <Input type="number" min={0} max={120} value={form.minutos_antes_checado} onChange={numero("minutos_antes_checado")} />
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Hueco máximo dentro de un bloque (min)</Label>
          <Input type="number" min={0} max={180} value={form.minutos_hueco_tramo} onChange={numero("minutos_hueco_tramo")} />
          <p className="text-xs text-slate-500">Clases separadas por un receso de hasta estos minutos se checan como un solo bloque. Un hueco mayor es hora muerta: se checa salida y luego entrada.</p>
        </div>
        <div className="space-y-1">
          <Label className="label-uppercase">Tarifa por hora-clase (opcional)</Label>
          <Input type="number" min={0} step="0.01" value={form.tarifa_hora_clase ?? ""} onChange={numero("tarifa_hora_clase")} placeholder="Sin tarifa" />
          <p className="text-xs text-slate-500">Se usa para estimar importes; cada docente puede tener la suya.</p>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="label-uppercase">Checado desde el portal web</Label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.checado_web} onChange={(e) => setForm({ ...form, checado_web: e.target.checked })} data-testid="esc-checado-web" />
            Los docentes pueden checar entrada y salida desde su portal (además del checador)
          </label>
          <label className={`flex items-center gap-2 text-sm ${form.checado_web ? "" : "opacity-50"}`}>
            <input type="checkbox" disabled={!form.checado_web} checked={!!form.requiere_ubicacion_web} onChange={(e) => setForm({ ...form, requiere_ubicacion_web: e.target.checked })} />
            Pedir ubicación: una marca lejos del plantel se guarda pero no cuenta para las clases
          </label>
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="esc-config-guardar">
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

export default function Escuela() {
  const { empresaActivaId } = useEmpresa();
  const [centros, setCentros] = useState([]);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  useEffect(() => {
    api.get("/centros").then((r) => setCentros((r.data || []).filter((c) => c.activo !== false)));
  }, [empresaActivaId]);

  return (
    <div className="p-6 md:p-10" data-testid="escuela-page">
      <PageHeader
        kicker="Escuela"
        title="Configuración escolar"
        subtitle="Ciclos, módulos, materias y grupos que se usan para armar la carga horaria de los docentes."
        actions={<TourHelpButton onClick={startTour} />}
      />
      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />
      <Tabs defaultValue="configuracion">
        <TabsList className="mb-4 flex-wrap h-auto" data-tour="esc-tabs">
          <TabsTrigger value="configuracion" data-tour="esc-tab-configuracion">Reglas</TabsTrigger>
          <TabsTrigger value="ciclos" data-tour="esc-tab-ciclos">Ciclos</TabsTrigger>
          <TabsTrigger value="modulos" data-tour="esc-tab-modulos">Módulos</TabsTrigger>
          <TabsTrigger value="materias" data-tour="esc-tab-materias">Materias</TabsTrigger>
          <TabsTrigger value="grupos" data-tour="esc-tab-grupos">Grupos</TabsTrigger>
        </TabsList>
        <TabsContent value="configuracion"><ConfiguracionTab empresaActivaId={empresaActivaId} /></TabsContent>
        {["ciclos", "modulos", "materias", "grupos"].map((c) => (
          <TabsContent key={c} value={c}>
            <CatalogoTab catalogo={c} centros={centros} empresaActivaId={empresaActivaId} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
